// src/components/resources/glossary/FieldContentRenderer.tsx

/**
 * Field Content Renderer Component
 *
 * A flexible component for rendering different types of field content based on
 * the GlossaryFieldContent types. This component is designed to handle all the
 * different field content patterns defined in the glossary system.
 *
 * @module FieldContentRenderer
 */

'use client'

import { motion } from 'motion/react'
import type {
  ContentSectionTypes,
  StringListSection,
  KeyValueListSection,
  NestedListSection,
  DefinitionListSection,
  GroupedExamplesSection
} from '~/lib/resources/types/glossary/field-types'
import {
  isStringListSection,
  isKeyValueListSection,
  isNestedListSection,
  isDefinitionListSection,
  isGroupedExamplesSection
} from '~/lib/resources/types/glossary/field-types'

interface FieldContentRendererProps {
  title: string
  content: ContentSectionTypes
}

/**
 * Renders a string list section with bullet points
 */
function StringListRenderer({ content }: { content: StringListSection }): JSX.Element {
  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50"
      aria-label={content.sectionName}
    >
      <ul className="space-y-6" role="list">
        {content.items.map((item: string, index: number) => (
          <motion.li
            key={index}
            className="flex items-start gap-3 group relative pl-4 before:absolute before:left-0 before:top-0
                     before:h-full before:w-[2px] before:bg-emerald-100 dark:before:bg-emerald-900/20
                     before:rounded-full group-hover:before:bg-emerald-200 dark:group-hover:before:bg-emerald-800/30"
            whileHover={{ scale: 1.01, x: 4 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full
                         bg-emerald-50/80 text-sm font-medium text-emerald-600
                         group-hover:bg-emerald-100 group-hover:text-emerald-700
                         dark:bg-emerald-900/20 dark:text-emerald-400
                         dark:group-hover:bg-emerald-900/30 dark:group-hover:text-emerald-300
                         transition-all ring-1 ring-emerald-100 dark:ring-emerald-800
                         group-hover:ring-emerald-200 dark:group-hover:ring-emerald-700"
              aria-hidden="true"
            >
              {index + 1}
            </div>
            <p className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-200
                       transition-colors leading-relaxed pt-0.5">
              {item}
            </p>
          </motion.li>
        ))}
      </ul>
    </section>
  )
}

/**
 * Renders a key-value list section as a definition list
 */
function KeyValueListRenderer({ content }: { content: KeyValueListSection<Record<string, string>> }): JSX.Element {
  const { primaryKey, secondaryKey } = content.keyInfo
  const { layout = { columns: 1 } } = content.display

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50"
      aria-label={content.sectionName}
    >
      <dl className={`grid gap-8 ${layout.columns === 2 ? 'sm:grid-cols-2' : ''}`}>
        {content.items.map((item, index) => (
          <motion.div
            key={index}
            className="group relative pl-4 before:absolute before:left-0 before:top-0
                     before:h-full before:w-[2px] before:bg-indigo-100 dark:before:bg-indigo-900/20
                     before:rounded-full group-hover:before:bg-indigo-200 dark:group-hover:before:bg-indigo-800/30"
            whileHover={{ scale: 1.01, x: 4 }}
            transition={{ duration: 0.2 }}
          >
            <dt className="flex items-center gap-2 mb-3">
              <span className="h-2 w-2 rounded-full bg-indigo-400/60 ring-4 ring-indigo-50 dark:ring-indigo-900/20
                           group-hover:bg-indigo-500 dark:bg-indigo-400/40
                           transition-all duration-300"
                    aria-hidden="true" />
              <span className="text-base font-medium text-gray-900 dark:text-white
                           group-hover:text-indigo-600 dark:group-hover:text-indigo-400
                           transition-colors">
                {item[primaryKey]}
              </span>
            </dt>
            <dd className="ml-6 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-300
                       transition-colors leading-relaxed prose-sm prose dark:prose-invert max-w-none">
              {item[secondaryKey]}
            </dd>
          </motion.div>
        ))}
      </dl>
    </section>
  )
}

/**
 * Renders a nested list section with grouped items
 */
function NestedListRenderer({ content }: { content: NestedListSection }): JSX.Element {
  const { mainKey, listKey } = content.keyInfo
  const { layout = { nestedStyle: 'indented' } } = content.display

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50"
      aria-label={content.sectionName}
    >
      <div className="space-y-12">
        {content.items.map((item, index) => {
          const list = item[listKey] as readonly string[]
          return (
            <motion.div
              key={index}
              className="group relative pl-4 before:absolute before:left-0 before:top-0
                       before:h-full before:w-[2px] before:bg-blue-100 dark:before:bg-blue-900/20
                       before:rounded-full group-hover:before:bg-blue-200 dark:group-hover:before:bg-blue-800/30"
              whileHover={{ scale: 1.01, x: 4 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="h-2.5 w-2.5 rounded-full bg-blue-400/60 ring-4 ring-blue-50 dark:ring-blue-900/20
                             group-hover:bg-blue-500 dark:bg-blue-400/40
                             transition-all duration-300"
                      aria-hidden="true" />
                <h4 className="text-base font-medium text-gray-900 dark:text-white
                           group-hover:text-blue-600 dark:group-hover:text-blue-400
                           transition-colors">
                  {item[mainKey]}
                </h4>
              </div>
              <div className="relative ml-6 pl-4 before:absolute before:left-0 before:top-0
                           before:h-full before:w-[1px] before:bg-blue-100/50 dark:before:bg-blue-900/10">
                <ul className={`grid gap-3 ${layout.nestedStyle === 'cards' ? 'sm:grid-cols-2' : ''}`} role="list">
                  {Array.from(list).map((subItem, subIndex) => (
                    <li
                      key={subIndex}
                      className="flex items-center gap-2 group/item"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-blue-300/40
                                   group-hover:bg-blue-400/60 dark:bg-blue-400/20
                                   transition-all duration-300"
                            aria-hidden="true" />
                      <span className="text-gray-600 dark:text-gray-400
                                   group-hover:text-gray-900 dark:group-hover:text-gray-300
                                   transition-colors leading-relaxed">
                        {subItem}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )
        })}
      </div>
    </section>
  )
}

/**
 * Renders a definition list section with name/description pairs
 */
function DefinitionListRenderer({ content }: { content: DefinitionListSection }): JSX.Element {
  const { layout = { columns: 1, compact: false } } = content.display

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50"
      aria-label={content.sectionName}
    >
      <dl className={`grid gap-8 ${layout.columns === 2 ? 'sm:grid-cols-2' : ''}`}>
        {content.items.map((item, index) => (
          <motion.div
            key={index}
            className={`group relative pl-4 before:absolute before:left-0 before:top-0
                     before:h-full before:w-[2px] before:bg-violet-100 dark:before:bg-violet-900/20
                     before:rounded-full group-hover:before:bg-violet-200 dark:group-hover:before:bg-violet-800/30
                     ${layout.compact ? 'space-y-2' : 'space-y-3'}`}
            whileHover={{ scale: 1.01, x: 4 }}
            transition={{ duration: 0.2 }}
          >
            <dt className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-violet-400/60 ring-4 ring-violet-50 dark:ring-violet-900/20
                           group-hover:bg-violet-500 dark:bg-violet-400/40
                           transition-all duration-300"
                    aria-hidden="true" />
              <span className="text-base font-medium text-gray-900 dark:text-white
                           group-hover:text-violet-600 dark:group-hover:text-violet-400
                           transition-colors">
                {item.name}
              </span>
            </dt>
            <dd className="ml-6 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-300
                       transition-colors leading-relaxed prose-sm prose dark:prose-invert max-w-none">
              {item.description}
            </dd>
          </motion.div>
        ))}
      </dl>
    </section>
  )
}

/**
 * Renders a grouped examples section with name/examples pairs
 */
function GroupedExamplesRenderer({ content }: { content: GroupedExamplesSection }): JSX.Element {
  const { layout = { nestedStyle: 'indented', gridLayout: true } } = content.display

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50"
      aria-label={content.sectionName}
    >
      <div className="space-y-12">
        {content.items.map((item, index) => (
          <motion.div
            key={index}
            className="group relative pl-4 before:absolute before:left-0 before:top-0
                     before:h-full before:w-[2px] before:bg-emerald-100 dark:before:bg-emerald-900/20
                     before:rounded-full group-hover:before:bg-emerald-200 dark:group-hover:before:bg-emerald-800/30"
            whileHover={{ scale: 1.01, x: 4 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/60 ring-4 ring-emerald-50 dark:ring-emerald-900/20
                           group-hover:bg-emerald-500 dark:bg-emerald-400/40
                           transition-all duration-300"
                    aria-hidden="true" />
              <h4 className="text-base font-medium text-gray-900 dark:text-white
                         group-hover:text-emerald-600 dark:group-hover:text-emerald-400
                         transition-colors">
                {item.name}
              </h4>
            </div>
            <div className="relative ml-6 pl-4 before:absolute before:left-0 before:top-0
                         before:h-full before:w-[1px] before:bg-emerald-100/50 dark:before:bg-emerald-900/10">
              <ul className={`grid gap-3 ${layout.gridLayout ? 'sm:grid-cols-2' : ''}`} role="list">
                {Array.from(item.examples).map((example, exampleIndex) => (
                  <li
                    key={exampleIndex}
                    className="flex items-center gap-2 group/item"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-300/40
                                 group-hover:bg-emerald-400/60 dark:bg-emerald-400/20
                                 transition-all duration-300"
                          aria-hidden="true" />
                    <span className="text-gray-600 dark:text-gray-400
                                 group-hover:text-gray-900 dark:group-hover:text-gray-300
                                 transition-colors leading-relaxed">
                      {example}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  )
}

/**
 * Main component that renders field content based on its type
 */
export function FieldContentRenderer({ title, content }: FieldContentRendererProps): JSX.Element {
  if (isStringListSection(content)) {
    return <StringListRenderer content={content} />
  }

  if (isDefinitionListSection(content)) {
    return <DefinitionListRenderer content={content} />
  }

  if (isGroupedExamplesSection(content)) {
    return <GroupedExamplesRenderer content={content} />
  }

  if (isKeyValueListSection(content)) {
    return <KeyValueListRenderer content={content} />
  }

  if (isNestedListSection(content)) {
    return <NestedListRenderer content={content} />
  }

  // Fallback for unknown content types
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50">
      <h3 className="text-xl font-semibold mb-4">{title}</h3>
      <pre className="text-sm text-gray-600 dark:text-gray-400">
        {JSON.stringify(content, null, 2)}
      </pre>
    </div>
  )
}
